import { useEffect, useState, useRef, useCallback } from "react";
import { ReactComponent as Logo } from "../../../../assets/icons/animation-logo.svg";
import { ReactComponent as ArrowCircleIcon } from "../../../../assets/icons/arrow-circle-right.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/x-close.svg";
import { useApp } from "../../../../services/app-context";
import { SignedUrlImage } from "../../../../components/SignedUrlImage";
import { useNavigate } from "react-router-dom";
import { getLabel, getUrl } from "../../utils/labels";
import { useMicPermissionWarmup } from "../../../../services/mic-permission";

interface CertificationIntroProps {
  assignment: any;
  primerText?: string;
  onBegin: () => void | Promise<void>;
}

export function CertificationIntro({ assignment, primerText, onBegin }: CertificationIntroProps) {
  const navigate = useNavigate();
  const { tenantProfile, styles, certificationAssets } = useApp();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const labels = certificationAssets?.labels ?? {};
  const finalPrimerText = primerText || getLabel("defaultPrimer", labels);
  const routeNames = tenantProfile?.routeNames ?? {};
  const url = getUrl("certificationRoute", routeNames);
  
  const handleClose = useCallback(() => {
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    navigate(`/${url}`);
  }, [navigate, url]);


  // Request microphone permission early so the browser prompts the user before recording starts
  useMicPermissionWarmup("certification intro screen");

  // Play audio when component mounts
  useEffect(() => {
    const audioUrl = assignment?.primer?.audioUrl;
    
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      // Set up audio event listeners
      audio.onplay = () => setIsAudioPlaying(true);
      audio.onpause = () => setIsAudioPlaying(false);
      audio.onended = () => setIsAudioPlaying(false);
      
      audio.play().catch((error) => {
        console.error("Failed to play audio:", error);
        setIsAudioPlaying(false);
      });

      // Cleanup function
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setIsAudioPlaying(false);
      };
    }
  }, [assignment?.primer?.audioUrl]);

  return (
    <div className="fixed inset-0 bg-background flex">
      {/* Main Content Area */}
      <div className="flex flex-col w-full">
        {/* Header Section */}
        <div className="p-6 flex justify-between items-start gap-8">
          <div className="flex flex-col gap-2 flex-1">
            <h1 className="text-body font-pp-semibold text-text-primary">{getLabel("certification.singular", labels)}</h1>
            <p className="text-caption1 font-pp-medium text-text2">
              {getLabel("session.singular", labels)}: {assignment?.certificationName || "Loading..."}
            </p>
            <p className="text-caption1 font-pp-medium text-text2">
              {getLabel("attempt.singular", labels)} #: {assignment?.attemptNumber || 1}{assignment?.maxAttempts ? `/${assignment.maxAttempts}` : ''}
            </p>
          </div>

          {/* Close Button */}
          <div className="flex items-center gap-3 flex-shrink-0 relative">
          <button 
            onClick={handleClose}
            className="w-12 h-12 bg-white border border-gray-300 rounded-full shadow-sm flex items-center justify-center hover:bg-gray-50 transition-all"
          >
            <CloseIcon className="w-5 h-5 [&_path]:stroke-gray-400" />
          </button>
          </div>
        </div>

        {/* Center Content */}
        <div className="flex-1 flex flex-col items-center justify-center gap-8 px-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <h2 className="text-subheadline font-pp-medium text-text-primary">{getLabel("certification.singular", labels)} Intro</h2>
          <p className="max-w-4xl text-text2 text-small font-pp-medium">
            {finalPrimerText}
          </p>
        </div>

        {/* Logo Circle */}
        <div className="relative w-64 h-64 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center shadow-lg">
          <div 
            className={`w-56 h-56 rounded-full bg-white flex items-center justify-center transition-all duration-200 certification-logo-circle ${
              isAudioPlaying ? 'playing' : ''
            }`}
          >
            {styles?.logo ? (
              <SignedUrlImage
                s3Key={styles?.smallLogo}
                bucket={tenantProfile?.assets_bucket}
                alt="logo"
                className="w-32 h-32 object-contain"
              />
            ) : (
              <Logo className="w-32 h-32" />
            )}
          </div>
        </div>

        {/* Begin Button */}
        <button
          onClick={onBegin}
          className="mt-8 h-[51px] px-4 bg-white hover:bg-gray-50 text-gray-800 rounded-full flex items-center justify-center gap-3 text-small font-pp-regular shadow-lg transition-all border border-gray-200"
        >
          <ArrowCircleIcon className="w-7 h-7" />
          Begin {getLabel("certification.singular", labels)}
        </button>
        </div>
      </div>
    </div>
  );
}

